<template>
  <div
    v-intersect.once="{
      handler: onIntersect,
      options: {
        threshold: [0, 0.5, 1.0]
      }
    }">
    <v-skeleton-loader :loading="isLoading" type="text" style="min-width: 75px">
      <span
        v-bind="$attrs"
        class="d-inline-flex flex-column gap-1 justify-start typography-body-md text-break">
        <span v-if="showName">{{ userName }}</span>
        <span v-if="showEmail">{{ userEmail }}</span>
        <span v-if="showPhone">{{ userPhone }}</span>
      </span>
    </v-skeleton-loader>
  </div>
</template>

<script>
import { ref, computed, watch, toRefs, unref } from 'vue';
import userService from '@satellite/services/user-service';

import { namesToFullName } from '@nova/core';

export default {
  name: 'UserPresenter',
  inheritAttrs: false,
  props: {
    user: {
      type: Object,
      required: false,
      default: null,
      validator(value) {
        if (!value) {
          return true;
        }
        return value?.id || (value?.firstName && value?.lastName);
      }
    },
    userId: {
      type: String,
      required: false,
      default: null
    },
    displayFields: {
      type: Array,
      default: () => ['name'],
      validator(value) {
        return value.every(field => ['name', 'email', 'phone'].includes(field));
      }
    }
  },
  setup(props) {
    const { userId, user } = toRefs(props);
    const fetchedUser = ref(null);
    const isLoading = ref(false);

    const displayFields = computed(() => {
      if (Array.isArray(props.displayFields)) {
        return props.displayFields;
      }

      return [];
    });

    const fetchUser = async id => {
      if (!id) {
        return;
      }

      try {
        isLoading.value = true;

        const response = await userService.getUserByIdV2(id, {
          enableCache: true
        });

        fetchedUser.value = response ?? null;
      } catch (error) {
        // TODO: add a retry mechanism?
        console.error('Failed to get user information:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const currentUser = computed(() => {
      return { ...user?.value, ...fetchedUser.value };
    });

    const userName = computed(() => {
      if (currentUser.value?.firstName || currentUser.value?.lastName) {
        return namesToFullName(currentUser.value?.firstName, currentUser.value?.lastName);
      }

      return '-';
    });

    const showName = computed(() => {
      return displayFields.value.includes('name');
    });

    const userEmail = computed(() => {
      if (currentUser.value?.email) {
        return currentUser.value?.email;
      }

      return '-';
    });

    const showEmail = computed(() => {
      return displayFields.value.includes('email');
    });

    const userPhone = computed(() => {
      if (currentUser.value?.phone) {
        return currentUser.value?.phone;
      }

      return '';
    });

    const showPhone = computed(() => {
      return Boolean(displayFields.value.includes('phone') && userPhone.value);
    });

    const fetchUserIfNecessary = () => {
      const currentUserIdValue = unref(userId);
      const currentUserValue = unref(user);
      const id = currentUserIdValue || currentUserValue?.id;

      if (!id) {
        return;
      }

      const needsToFetchName =
        (!currentUserValue?.firstName || !currentUserValue?.lastName) &&
        displayFields.value.includes('name');
      const needsToFetchEmail = !currentUserValue?.email && displayFields.value.includes('email');
      const needsToFetchPhone = !currentUserValue?.phone && displayFields.value.includes('phone');

      if (
        (needsToFetchName || needsToFetchEmail || needsToFetchPhone) &&
        fetchedUser.value?.id !== id
      ) {
        fetchUser(id);
      }
    };

    watch([() => unref(userId), () => unref(user)?.id], () => {
      fetchUserIfNecessary();
    });

    const onIntersect = () => {
      fetchUserIfNecessary();
    };

    return {
      isLoading,
      userName,
      userEmail,
      userPhone,
      showName,
      showEmail,
      showPhone,
      onIntersect
    };
  }
};
</script>

<style scoped>
:deep(.v-skeleton-loader__text) {
  /** this value makes the loader have the same vertical spacing as the user text */
  margin: 6px 0 !important;
}
</style>
